import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const PUBLISHER_URL = 'https://link.springer.com/book/10.1007/979-8-8688-3059-4';
const BOOK_URL = 'https://platform0.ca/book';

const Page = styled.div`
  padding-top: 80px;
`;

const Hero = styled.section`
  background: var(--black);
  color: var(--white);
  padding: 5rem 0;
`;

const HeroInner = styled.div`
  width: min(1120px, calc(100% - 4rem));
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(240px, 0.8fr);
  align-items: center;
  gap: 4rem;

  @media (max-width: 768px) {
    width: min(100% - 2rem, 600px);
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`;

const HeroCopy = styled.div`
  order: 1;
  @media (max-width: 768px) { order: 2; }
`;

const Eyebrow = styled.p`
  color: var(--primary);
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-size: 0.85rem;
  margin-bottom: 1rem;
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 5vw, 4rem);
  line-height: 1.08;
  margin-bottom: 1rem;
  span { display: block; color: var(--primary); }
`;

const Subtitle = styled.p`
  color: var(--gray-300);
  font-size: clamp(1.1rem, 2vw, 1.35rem);
  line-height: 1.55;
  margin-bottom: 1.25rem;
  max-width: 660px;
`;

const Byline = styled.p`
  color: var(--gray-400);
  margin-bottom: 2rem;
  strong { color: var(--white); }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
`;

const PrimaryAction = styled.a`
  display: inline-block;
  border-radius: 4px;
  padding: 0.9rem 1.5rem;
  background: var(--primary);
  color: var(--black);
  font-weight: 700;
  text-align: center;
  &:hover { background: var(--primary-dark); color: var(--black); }
  &:focus-visible { outline: 3px solid var(--white); outline-offset: 3px; }
`;

const TextAction = styled.a`
  color: var(--white);
  text-decoration: underline;
  text-underline-offset: 4px;
  &:hover { color: var(--primary); }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 4px; }
`;

const CoverFrame = styled.div`
  order: 2;
  width: min(100%, 330px);
  justify-self: center;
  padding: 0.75rem;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.14);
  border-radius: 8px;
  box-shadow: 0 24px 60px rgba(0,0,0,0.35);
  img { display: block; width: 100%; height: auto; border-radius: 2px; }
  @media (max-width: 768px) { order: 1; width: min(68%, 270px); }
`;

const Section = styled.section`
  padding: 5rem 0;
  background: ${props => props.$muted ? 'var(--gray-100)' : 'var(--white)'};
  @media (max-width: 768px) { padding: 3.5rem 0; }
`;

const Inner = styled.div`
  width: min(1080px, calc(100% - 4rem));
  margin: 0 auto;
  @media (max-width: 768px) { width: min(100% - 2rem, 600px); }
`;

const SectionHeading = styled.h2`
  color: var(--black);
  font-size: clamp(1.8rem, 3vw, 2.5rem);
  margin-bottom: 1rem;
`;

const Lead = styled.p`
  color: var(--gray-700);
  font-size: 1.1rem;
  line-height: 1.8;
  max-width: 850px;
  margin-bottom: 0;
`;

const LearningGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem;
  margin-top: 2rem;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const LearningCard = styled.article`
  padding: 1.5rem;
  background: var(--white);
  border: 1px solid var(--gray-200);
  border-radius: 8px;
  border-top: 3px solid var(--primary);
  h3 { font-size: 1.15rem; margin-bottom: 0.5rem; }
  p { color: var(--gray-700); line-height: 1.65; margin: 0; }
`;

const AudienceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem 2rem;
  margin: 1.5rem 0 0;
  padding: 0;
  list-style: none;
  li { padding: 0.8rem 0 0.8rem 1.8rem; position: relative; color: var(--gray-700); line-height: 1.6; }
  li:before { content: '•'; color: var(--primary-dark); font-size: 1.5rem; position: absolute; left: 0; top: 0.5rem; }
  strong { color: var(--gray-900); }
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const AuthorLayout = styled.div`
`;

const BottomCta = styled.section`
  padding: 4.5rem 0;
  text-align: center;
  color: var(--white);
  background: var(--black);
  h2 { font-size: clamp(1.8rem, 3vw, 2.5rem); margin-bottom: 0.75rem; }
  p { color: var(--gray-300); max-width: 650px; margin: 0 auto 1.75rem; line-height: 1.7; }
`;

function BookPage() {
  useEffect(() => {
    const previousTitle = document.title;
    const description = 'Discover Without Chaos: The AI Catalyst by Bernard Bardawil, a practical guide to building platforms, teams, and strategy for sustainable enterprise AI.';
    document.title = 'Without Chaos: The AI Catalyst | Platform0';

    const setMeta = (selector, attributes) => {
      let element = document.head.querySelector(selector);
      const created = !element;
      if (!element) {
        element = document.createElement('meta');
        document.head.appendChild(element);
      }
      const previous = Object.fromEntries(Object.keys(attributes).map(key => [key, element.getAttribute(key)]));
      Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
      return { element, created, previous };
    };
    const setLink = (selector, attributes) => {
      let element = document.head.querySelector(selector);
      const created = !element;
      if (!element) {
        element = document.createElement('link');
        document.head.appendChild(element);
      }
      const previous = Object.fromEntries(Object.keys(attributes).map(key => [key, element.getAttribute(key)]));
      Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
      return { element, created, previous };
    };

    const metas = [
      setMeta('meta[name="description"]', { name: 'description', content: description }),
      setMeta('meta[property="og:type"]', { property: 'og:type', content: 'book' }),
      setMeta('meta[property="og:title"]', { property: 'og:title', content: 'Without Chaos: The AI Catalyst' }),
      setMeta('meta[property="og:description"]', { property: 'og:description', content: description }),
      setMeta('meta[property="og:url"]', { property: 'og:url', content: BOOK_URL }),
      setMeta('meta[property="og:image"]', { property: 'og:image', content: 'https://platform0.ca/images/without-chaos-cover.jpg' }),
      setMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' }),
      setMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: 'Without Chaos: The AI Catalyst' }),
      setMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description }),
      setMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: 'https://platform0.ca/images/without-chaos-cover.jpg' })
    ];
    const canonical = setLink('link[rel="canonical"]', { rel: 'canonical', href: BOOK_URL });

    return () => {
      document.title = previousTitle;
      metas.forEach(({ element, created }) => {
        if (created) element.remove();
        else {
          const previous = metas.find(meta => meta.element === element).previous;
          Object.entries(previous).forEach(([key, value]) => value === null ? element.removeAttribute(key) : element.setAttribute(key, value));
        }
      });
      if (canonical.created) canonical.element.remove();
      else Object.entries(canonical.previous).forEach(([key, value]) => value === null ? canonical.element.removeAttribute(key) : canonical.element.setAttribute(key, value));
    };
  }, []);

  return (
    <Page>
      <Hero>
        <HeroInner>
          <HeroCopy>
            <Eyebrow>A book by Bernard Bardawil</Eyebrow>
            <Title>Without Chaos:<span>The AI Catalyst</span></Title>
            <Subtitle>How to Build Platforms, Teams, and Strategy for Enterprise-Scale AI Success</Subtitle>
            <Byline>By <strong>Bernard Bardawil</strong></Byline>
            <Actions>
              <PrimaryAction href={PUBLISHER_URL} target="_blank" rel="noopener noreferrer">Buy now</PrimaryAction>
              <TextAction href="#what-youll-learn">Explore what you’ll learn</TextAction>
            </Actions>
          </HeroCopy>
          <CoverFrame>
            <img src="/images/without-chaos-cover.jpg" alt="Cover of Without Chaos: The AI Catalyst by Bernard Bardawil" />
          </CoverFrame>
        </HeroInner>
      </Hero>

      <Section>
        <Inner>
          <SectionHeading>Turn AI ambition into a lasting capability</SectionHeading>
          <Lead>AI adoption is accelerating, yet many organizations face confusion and stalled progress. Without Chaos: The AI Catalyst addresses the gap between scattered experiments and a sustainable enterprise AI practice. At the intersection of strategy and architecture, it offers leaders, architects, and decision-makers a structured approach to scaling AI responsibly.</Lead>
        </Inner>
      </Section>

      <Section id="what-youll-learn" $muted>
        <Inner>
          <SectionHeading>What you’ll learn</SectionHeading>
          <Lead>The book combines strategic mapping, platform engineering, organizational design, and architectural patterns with practical tools and maturity models.</Lead>
          <LearningGrid>
            <LearningCard><h3>Build an adoption strategy</h3><p>Prioritize use cases and build a practical AI adoption strategy that avoids common pitfalls.</p></LearningCard>
            <LearningCard><h3>Design platforms and paved paths</h3><p>Apply platform engineering principles to create a minimal viable platform and reusable paths for teams.</p></LearningCard>
            <LearningCard><h3>Align teams and architecture</h3><p>Map opportunities using Wardley Mapping, value streams, and team topologies, then connect organizational design to architecture.</p></LearningCard>
            <LearningCard><h3>Establish trust and keep learning</h3><p>Embed trust, accountability, and governance from day one, and develop continuous learning as AI capabilities scale.</p></LearningCard>
          </LearningGrid>
        </Inner>
      </Section>

      <Section>
        <Inner>
          <SectionHeading>Who is this book for?</SectionHeading>
          <Lead>Written for leaders and decision-makers responsible for setting direction, shaping architecture, and scaling AI responsibly across an organization.</Lead>
          <AudienceGrid>
            <li><strong>CTOs and technology executives</strong> setting AI direction and building lasting organizational capability.</li>
            <li><strong>Chief, enterprise, and platform architects</strong> connecting AI strategy to architecture and platform decisions.</li>
            <li><strong>AI and platform leaders</strong> shaping how teams, governance, and delivery work across the organization.</li>
            <li><strong>Engineering leaders and practitioners</strong> including principal engineers, engineering managers, tech leads, AI/ML platform teams, and MLOps engineers.</li>
          </AudienceGrid>
        </Inner>
      </Section>

      <Section $muted>
        <Inner>
          <AuthorLayout>
            <SectionHeading>About the author</SectionHeading>
            <Lead>Bernard Bardawil is a technology architect and engineering leader with more than 15 years of experience designing and scaling digital platforms, enterprise architecture, and AI-driven systems. He has led technology transformation initiatives across platform engineering, data platforms, cloud modernization, and applied AI, helping organizations move from fragmented experimentation to scalable operational capabilities. His work focuses on the intersection of architecture, platform strategy, organizational design, and enterprise AI adoption. Bernard is particularly known for translating complex technical concepts into practical operating models that enable teams to deliver AI responsibly and at scale. Through both hands-on engineering leadership and strategic advisory work, he has guided organizations in building sustainable technology foundations, modern delivery practices, and production-grade AI capabilities.</Lead>
          </AuthorLayout>
        </Inner>
      </Section>

      <BottomCta>
        <Inner>
          <h2>Build AI capability without the chaos</h2>
          <p>Explore the book’s full description, chapter list, formats, and purchase options on the publisher’s page.</p>
          <Actions style={{ justifyContent: 'center' }}>
            <PrimaryAction href={PUBLISHER_URL} target="_blank" rel="noopener noreferrer">Buy now</PrimaryAction>
            <TextAction as={Link} to="/contact">Talk with Platform0</TextAction>
          </Actions>
        </Inner>
      </BottomCta>
    </Page>
  );
}

export default BookPage;
